import React from 'react';
import { MediaHelpers } from '@wajeez-web/core/api';
import { Skeleton } from 'antd';

import {
  BaseComponentProps,
  useComponentData,
  useSkeletonHelper,
} from '../helpers/contentHooks';
import { ButtonHeadless } from '@wajeez-web/components/ui';

export interface WajeezCardParameters {
  background: string | null;
  bottomPadding: string | null;
  buttonFontSize: string | null;
  buttonTitle: string | null;
  cornerRadius: string | null;
  endPadding: string | null;
  fontSize: string | null;
  height: string | null;
  horizontalSpacing: string | null;
  icon: string | null;
  link: string | null;
  showDivider: string | null;
  startPadding: string | null;
  text: string | null;
  textColor: string | null;
  title: string | null;
  topPadding: string | null;
  verticalSpacing: string | null;
}

export function WajeezCard({ component, context }: BaseComponentProps) {
  const { getParameterValue, contentsRequestStatus } =
    useComponentData<WajeezCardParameters>(component, context);

  const { isActive } = useSkeletonHelper(contentsRequestStatus);

  const background = getParameterValue('background') || '';
  const bottomPadding = getParameterValue('bottomPadding') || '0';
  const buttonFontSize = getParameterValue('buttonFontSize') || '12';
  const buttonTitle = getParameterValue('buttonTitle') || '';
  const cornerRadius = getParameterValue('cornerRadius') || '0';
  const endPadding = getParameterValue('endPadding') || '0';
  const fontSize = getParameterValue('fontSize') || '12';
  const height = getParameterValue('height') || '88';
  const horizontalSpacing = getParameterValue('horizontalSpacing');
  const icon = getParameterValue('icon') || '';
  const link = getParameterValue('link') || '';
  const showDivider = getParameterValue('showDivider') || 'false';
  const startPadding = getParameterValue('startPadding') || '0';
  const text = getParameterValue('text') || '';
  const textColor = getParameterValue('textColor') || 'white';
  const title = getParameterValue('title') || '';
  const topPadding = getParameterValue('topPadding') || '0';
  const verticalSpacing = getParameterValue('verticalSpacing');

  const isLoadingOrContent = getParameterValue('background');

  return isLoadingOrContent ? (
    <div className="w-full">
      {title && (
        <div className="font-bold text-[20px] sm:text-[26px] sm:text-[#27160E]">
          {title}
        </div>
      )}
      <div
        className={`sm:hidden relative mt-[16px] mx-4 sm:mx-0 overflow-hidden ${
          !buttonTitle && 'cursor-pointer'
        }`}
        style={{
          height: height + 'px',
          borderRadius: cornerRadius + 'px',
          WebkitTapHighlightColor: 'transparent',
        }}
        onClick={() => {
          if (!buttonTitle) {
            window.location.href = link;
          }
        }}
      >
        <img
          src={MediaHelpers.getPublicMediaById(background)}
          alt="wajeez-card"
          className="w-full h-full"
        />
        <div
          className="absolute top-0 left-0 w-full h-full flex justify-start items-center"
          style={{
            paddingTop: topPadding + 'px',
            paddingBottom: bottomPadding + 'px',
            paddingRight: startPadding + 'px',
            paddingLeft: endPadding + 'px',
          }}
        >
          {text && (
            <div>
              <p style={{ color: textColor, fontSize }}>{text}</p>
              {buttonTitle && (
                <div style={{ marginTop: verticalSpacing + 'px' }}>
                  <ButtonHeadless
                    bgStyle="bg-primary"
                    borderRadiusStyle="rounded-[8px]"
                    heightStyle="h-[40px]"
                    paddingYStyle="py-[11px]"
                    action={() => {
                      window.location.href = link;
                    }}
                  >
                    <div style={{ fontSize: buttonFontSize + 'px' }}>
                      {buttonTitle}
                    </div>
                  </ButtonHeadless>
                </div>
              )}
            </div>
          )}
          {icon && (
            <div
              className="h-full"
              style={{ marginRight: horizontalSpacing + 'px' }}
            >
              <img
                src={MediaHelpers.getPublicMediaById(icon)}
                alt="icon"
                className="w-full h-full"
              />
            </div>
          )}
        </div>
      </div>
      <div className="hidden sm:block text-[15px] font-medium text-[#424242]">
        {text}
      </div>
    </div>
  ) : contentsRequestStatus === 'loading' &&
    !getParameterValue('background') ? (
    <WajeezCardSkeleton isActive={isActive} />
  ) : null;
}

const WajeezCardSkeleton = ({ isActive = true }: { isActive?: boolean }) => {
  return (
    <div className="w-full px-4">
      <Skeleton.Node
        active={isActive}
        className="!w-full !h-[162px] dark:bg-dark-medium"
      >
        <div />
      </Skeleton.Node>
    </div>
  );
};
